/**
 * Original vector geometry for all 34 Mahjong tile faces.
 *
 * Pure data, no React and no DOM: the same coordinates can drive the web SVG
 * renderer today and an Android canvas/vector drawable later. Nothing here is
 * traced from existing artwork; every face is built from primitives.
 *
 * Coordinate space: 60 x 84, face area roughly x 8..52, y 12..72.
 */

import { TILE_INFO } from "@kikik/mahjong-core";
import type { TileKind } from "@kikik/mahjong-core";

export const TILE_WIDTH = 60;
export const TILE_HEIGHT = 84;

/** Semantic ink colours; the renderer maps them to CSS custom properties. */
export type Ink = "ink" | "red" | "green" | "blue" | "gold";

export type Shape =
  | { type: "disc"; cx: number; cy: number; r: number; ink: Ink; ring?: boolean }
  | { type: "stick"; cx: number; cy: number; height: number; ink: Ink }
  | { type: "stroke"; d: string; ink: Ink; width?: number }
  | { type: "fill"; d: string; ink: Ink }
  | { type: "glyphBox"; x: number; y: number; w: number; h: number; ink: Ink };

/** Row/column layouts used by the dot and bamboo faces. */
function grid(
  columns: number,
  rows: number,
  options: { top?: number; bottom?: number; left?: number; right?: number } = {},
): { x: number; y: number }[] {
  const top = options.top ?? 18;
  const bottom = options.bottom ?? 68;
  const left = options.left ?? 15;
  const right = options.right ?? 45;
  const points: { x: number; y: number }[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      points.push({
        x: columns === 1 ? (left + right) / 2 : left + ((right - left) * column) / (columns - 1),
        y: rows === 1 ? (top + bottom) / 2 : top + ((bottom - top) * row) / (rows - 1),
      });
    }
  }
  return points;
}

function discs(points: { x: number; y: number }[], r: number, inks: Ink[]): Shape[] {
  return points.map((point, index) => ({
    type: "disc" as const,
    cx: point.x,
    cy: point.y,
    r,
    ink: inks[index % inks.length] ?? "blue",
    ring: true,
  }));
}

function sticks(points: { x: number; y: number }[], height: number, inks: Ink[]): Shape[] {
  return points.map((point, index) => ({
    type: "stick" as const,
    cx: point.x,
    cy: point.y,
    height,
    ink: inks[index % inks.length] ?? "green",
  }));
}

/** Circles: the traditional arrangements, in an original two-tone palette. */
function dotFace(rank: number): Shape[] {
  switch (rank) {
    case 1:
      return [
        { type: "disc", cx: 30, cy: 42, r: 16, ink: "blue", ring: true },
        { type: "disc", cx: 30, cy: 42, r: 9, ink: "red", ring: true },
        { type: "disc", cx: 30, cy: 42, r: 3.5, ink: "blue" },
      ];
    case 2:
      return discs(grid(1, 2, { top: 26, bottom: 60 }), 10, ["green", "blue"]);
    case 3:
      return discs(
        [
          { x: 17, y: 24 },
          { x: 30, y: 42 },
          { x: 43, y: 60 },
        ],
        9,
        ["blue", "green", "red"],
      );
    case 4:
      return discs(grid(2, 2, { top: 26, bottom: 60, left: 19, right: 41 }), 9, ["blue", "green"]);
    case 5:
      return [
        ...discs(grid(2, 2, { top: 24, bottom: 62, left: 17, right: 43 }), 8, ["blue", "green"]),
        { type: "disc", cx: 30, cy: 43, r: 8, ink: "red", ring: true },
      ];
    case 6:
      return discs(grid(2, 3, { top: 22, bottom: 64, left: 19, right: 41 }), 7.5, ["green", "red"]);
    case 7:
      return [
        ...discs(
          [
            { x: 16, y: 19 },
            { x: 27, y: 26 },
            { x: 38, y: 33 },
          ],
          6,
          ["green"],
        ),
        ...discs(grid(2, 2, { top: 48, bottom: 66, left: 20, right: 40 }), 6.5, ["red"]),
      ];
    case 8:
      return discs(grid(2, 4, { top: 20, bottom: 66, left: 20, right: 40 }), 6.5, ["blue"]);
    case 9:
    default:
      return discs(grid(3, 3, { top: 20, bottom: 66, left: 16, right: 44 }), 6.5, [
        "red",
        "red",
        "red",
        "blue",
        "blue",
        "blue",
        "green",
        "green",
        "green",
      ]);
  }
}

/** A simple original sparrow for 1 Bamboo; sticks for the rest. */
function bambooBird(): Shape[] {
  return [
    { type: "fill", d: "M30 16c6 0 11 5 11 11 0 6-4 9-4 13 0 5 5 8 5 14 0 7-6 12-12 12s-12-5-12-12c0-6 5-9 5-14 0-4-4-7-4-13 0-6 5-11 11-11z", ink: "green" },
    { type: "fill", d: "M30 18c3 0 5 2 5 5s-2 5-5 5-5-2-5-5 2-5 5-5z", ink: "red" },
    { type: "stroke", d: "M22 44c5 3 11 3 16 0", ink: "gold", width: 2 },
    { type: "stroke", d: "M24 56c4 3 8 3 12 0", ink: "gold", width: 2 },
    { type: "fill", d: "M30 66l4 8h-8z", ink: "red" },
  ];
}

function bambooFace(rank: number): Shape[] {
  switch (rank) {
    case 1:
      return bambooBird();
    case 2:
      return sticks(grid(1, 2, { top: 28, bottom: 58 }), 22, ["green", "blue"]);
    case 3:
      return sticks(
        [
          { x: 30, y: 26 },
          { x: 21, y: 58 },
          { x: 39, y: 58 },
        ],
        20,
        ["green", "blue", "blue"],
      );
    case 4:
      return sticks(grid(2, 2, { top: 28, bottom: 58, left: 21, right: 39 }), 20, ["green", "blue"]);
    case 5:
      return [
        ...sticks(grid(2, 2, { top: 26, bottom: 60, left: 19, right: 41 }), 18, ["green", "blue"]),
        ...sticks([{ x: 30, y: 43 }], 18, ["red"]),
      ];
    case 6:
      return sticks(grid(3, 2, { top: 28, bottom: 58, left: 17, right: 43 }), 18, ["green", "blue"]);
    case 7:
      return [
        ...sticks([{ x: 30, y: 22 }], 16, ["red"]),
        ...sticks(grid(3, 2, { top: 44, bottom: 64, left: 17, right: 43 }), 15, ["green", "blue"]),
      ];
    case 8:
      return sticks(grid(4, 2, { top: 28, bottom: 58, left: 14, right: 46 }), 18, ["green", "blue"]);
    case 9:
    default:
      return sticks(grid(3, 3, { top: 22, bottom: 64, left: 17, right: 43 }), 14, [
        "red",
        "red",
        "red",
        "green",
        "green",
        "green",
        "blue",
        "blue",
        "blue",
      ]);
  }
}

/**
 * Hand-built numerals for the nine character ranks.
 *
 * The rank has to live in the artwork, not only in the small corner index:
 * that index is dropped at the smallest tile size, which is exactly the size
 * used for discard piles, opponent melds and the status bar. Without a numeral
 * on the face all nine character tiles would be indistinguishable there.
 */
const CHARACTER_NUMERALS: Record<number, Shape[]> = {
  1: [{ type: "stroke", d: "M16 28h28", ink: "ink", width: 3.5 }],
  2: [
    { type: "stroke", d: "M19 21h22", ink: "ink", width: 3.2 },
    { type: "stroke", d: "M16 35h28", ink: "ink", width: 3.2 },
  ],
  3: [
    { type: "stroke", d: "M20 17h20", ink: "ink", width: 3 },
    { type: "stroke", d: "M22 27h16", ink: "ink", width: 3 },
    { type: "stroke", d: "M16 38h28", ink: "ink", width: 3 },
  ],
  4: [
    { type: "glyphBox", x: 18, y: 16, w: 24, h: 23, ink: "ink" },
    { type: "stroke", d: "M26 21v9", ink: "ink", width: 2.6 },
    { type: "stroke", d: "M34 21v6c0 3 2 4 4 3", ink: "ink", width: 2.6 },
  ],
  5: [
    { type: "stroke", d: "M17 17h26", ink: "ink", width: 3 },
    { type: "stroke", d: "M25 17v10", ink: "ink", width: 3 },
    { type: "stroke", d: "M23 27h15v10", ink: "ink", width: 3 },
    { type: "stroke", d: "M17 38h26", ink: "ink", width: 3 },
  ],
  6: [
    { type: "stroke", d: "M30 14v4", ink: "ink", width: 3 },
    { type: "stroke", d: "M17 23h26", ink: "ink", width: 3 },
    { type: "stroke", d: "M26 28l-7 11", ink: "ink", width: 3 },
    { type: "stroke", d: "M34 28l7 11", ink: "ink", width: 3 },
  ],
  7: [
    { type: "stroke", d: "M16 27h23", ink: "ink", width: 3 },
    { type: "stroke", d: "M26 15v18c0 4 3 6 8 5", ink: "ink", width: 3 },
  ],
  8: [
    { type: "stroke", d: "M28 17c-1 8-5 15-10 21", ink: "ink", width: 3 },
    { type: "stroke", d: "M32 17c1 8 5 15 10 21", ink: "ink", width: 3 },
  ],
  9: [
    { type: "stroke", d: "M18 21h13", ink: "ink", width: 3 },
    { type: "stroke", d: "M24 16c0 12-2 18-7 23", ink: "ink", width: 3 },
    { type: "stroke", d: "M33 16v17c0 4 3 6 8 5", ink: "ink", width: 3 },
  ],
};

/** The compact wan mark every character tile carries under its numeral. */
function wanMark(): Shape[] {
  return [
    { type: "stroke", d: "M19 54h22", ink: "red", width: 2.6 },
    { type: "stroke", d: "M25 48v7M35 48v7", ink: "red", width: 2.6 },
    { type: "glyphBox", x: 22, y: 57, w: 16, h: 10, ink: "red" },
    { type: "stroke", d: "M22 62h16M30 57v10", ink: "red", width: 1.8 },
    { type: "stroke", d: "M19 71h22", ink: "red", width: 2.6 },
    { type: "stroke", d: "M24 71l-3 5M36 71l3 5", ink: "red", width: 2.6 },
  ];
}

function characterFace(rank: number): Shape[] {
  return [...(CHARACTER_NUMERALS[rank] ?? []), ...wanMark()];
}

const HONOR_GLYPHS: Record<string, Shape[]> = {
  // 東 - East
  WIND_EAST: [
    { type: "stroke", d: "M16 26h28", ink: "ink", width: 3 },
    { type: "glyphBox", x: 20, y: 32, w: 20, h: 20, ink: "ink" },
    { type: "stroke", d: "M20 42h20", ink: "ink", width: 2.5 },
    { type: "stroke", d: "M30 20v40", ink: "ink", width: 3 },
    { type: "stroke", d: "M30 52l-10 12M30 52l10 12", ink: "ink", width: 3 },
  ],
  // 南 - South
  WIND_SOUTH: [
    { type: "stroke", d: "M30 18v6", ink: "ink", width: 3 },
    { type: "stroke", d: "M16 26h28", ink: "ink", width: 3 },
    { type: "stroke", d: "M18 26v36M42 26v36", ink: "ink", width: 3 },
    { type: "glyphBox", x: 24, y: 36, w: 12, h: 16, ink: "ink" },
    { type: "stroke", d: "M24 44h12M30 36v16", ink: "ink", width: 2 },
    { type: "stroke", d: "M18 62h24", ink: "ink", width: 3 },
  ],
  // 西 - West
  WIND_WEST: [
    { type: "stroke", d: "M16 24h28", ink: "ink", width: 3 },
    { type: "glyphBox", x: 18, y: 32, w: 24, h: 28, ink: "ink" },
    { type: "stroke", d: "M26 32v16M34 32v16", ink: "ink", width: 3 },
    { type: "stroke", d: "M18 48h24", ink: "ink", width: 2.5 },
  ],
  // 北 - North
  WIND_NORTH: [
    { type: "stroke", d: "M22 22v40", ink: "ink", width: 3 },
    { type: "stroke", d: "M22 34h-6M22 46h-6", ink: "ink", width: 3 },
    { type: "stroke", d: "M38 22v34", ink: "ink", width: 3 },
    { type: "stroke", d: "M38 34h8", ink: "ink", width: 3 },
    { type: "stroke", d: "M38 56l8 6", ink: "ink", width: 3 },
  ],
  // 中 - Red dragon
  DRAGON_RED: [
    { type: "glyphBox", x: 20, y: 28, w: 20, h: 26, ink: "red" },
    { type: "stroke", d: "M30 18v48", ink: "red", width: 3.5 },
  ],
  // 發 - Green dragon
  DRAGON_GREEN: [
    { type: "stroke", d: "M18 24h16", ink: "green", width: 3 },
    { type: "stroke", d: "M22 24v10M30 24v10", ink: "green", width: 3 },
    { type: "stroke", d: "M16 38h20", ink: "green", width: 3 },
    { type: "stroke", d: "M20 38l-4 12M32 38l4 12", ink: "green", width: 3 },
    { type: "glyphBox", x: 34, y: 30, w: 10, h: 12, ink: "green" },
    { type: "stroke", d: "M18 54h24M30 54v12", ink: "green", width: 3 },
    { type: "stroke", d: "M24 66h12", ink: "green", width: 3 },
  ],
  // 白 - White dragon: the classic empty frame
  DRAGON_WHITE: [
    { type: "glyphBox", x: 16, y: 20, w: 28, h: 44, ink: "blue" },
    { type: "stroke", d: "M22 26h16", ink: "blue", width: 2 },
  ],
};

/** Every shape that makes up the face of one tile kind. */
export function faceShapes(kind: TileKind): Shape[] {
  const info = TILE_INFO[kind];
  if (info.suit === "dots") return dotFace(info.rank);
  if (info.suit === "bamboo") return bambooFace(info.rank);
  if (info.suit === "characters") return characterFace(info.rank);
  return HONOR_GLYPHS[kind] ?? [];
}

/** Big rank numeral for the numbered suits; short Latin index for honors. */
export function faceIndex(kind: TileKind): string {
  const info = TILE_INFO[kind];
  if (!info.isHonor) return String(info.rank);
  const map: Record<string, string> = {
    WIND_EAST: "T",
    WIND_SOUTH: "S",
    WIND_WEST: "B",
    WIND_NORTH: "U",
    DRAGON_RED: "M",
    DRAGON_GREEN: "H",
    DRAGON_WHITE: "P",
  };
  return map[kind] ?? "?";
}

export function faceInk(kind: TileKind): Ink {
  const info = TILE_INFO[kind];
  switch (info.suit) {
    case "characters":
      return "red";
    case "bamboo":
      return "green";
    case "dots":
      return "blue";
    case "dragons":
      return kind === "DRAGON_RED" ? "red" : kind === "DRAGON_GREEN" ? "green" : "blue";
    default:
      return "ink";
  }
}
